import { Button, Modal, TextInput, Select } from "flowbite-react";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Loader from "../Common/Loader";
import { SubCategory } from "../../Models/SubCategory";
import { Category } from "../../Models/Category";
import CommonApi from "../../Api/CommonApi";

export default function EditSubCategoryModal({
  show,
  setShow,
  subCategory,
  categories,
  onRefresh,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  subCategory: SubCategory | null;
  categories: Category[];
  onRefresh: () => Promise<void> | undefined;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [formData, setFormData] = useState<SubCategory>({
    number: null,
    name: "",
    categoryId: null,
  });

  useEffect(() => {
    if (subCategory) {
      setFormData({
        number: subCategory.number,
        name: subCategory.name || "",
        categoryId: subCategory.categoryId,
      });
    } else {
      setFormData({
        number: null,
        name: "",
        categoryId: null,
      });
    }
  }, [subCategory]);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "number" ? parseInt(value, 10) || null : value,
    }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value ? parseInt(value) : null,
    }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    try {
      setIsLoading(true);
      return CommonApi.addUpdateSubCategory(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success("SubCategory updated successfully");
            setIsLoading(false);
            setButtonDisabled(false);
            onRefresh();
            setShow(false);
          } else {
            toast.error(res.data.message);
            setIsLoading(false);
            setButtonDisabled(false);
          }
        })
        .catch((err) => {
          toast.error("Error: " + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };

  function onCloseModal() {
    setShow(false);
  }

  return (
    <Modal show={show} size="md" onClose={onCloseModal} popup>
      <Modal.Header>
        {subCategory ? "Edit SubCategory" : "Add SubCategory"}
      </Modal.Header>
      <Modal.Body>
        <div className="w-full">
          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SubCategory Number
                </label>
                <TextInput
                  id="number"
                  name="number"
                  type="number"
                  placeholder="Enter subcategory number..."
                  onChange={handleChangeInput}
                  value={formData.number ?? ""}
                  sizing="md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  SubCategory Name
                </label>
                <TextInput
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter subcategory name..."
                  onChange={handleChangeInput}
                  value={formData.name ?? ""}
                  sizing="md"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category
                </label>
                <Select
                  id="categoryId"
                  name="categoryId"
                  onChange={handleChangeSelect}
                  value={formData.categoryId ?? ""}
                  sizing="md"
                  required
                >
                  <option value="">Select a category...</option>
                  {categories.map((category) => (
                    <option key={category.number} value={category.number}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex justify-end pt-4 space-x-2">
              <Button
                disabled={buttonDisabled}
                type="submit"
                className="buttonMain"
              >
                {isLoading ? <Loader /> : "Save"}
              </Button>
              <Button
                color="gray"
                onClick={onCloseModal}
                disabled={buttonDisabled}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
}
